import queryString from "query-string";
import fetch from "cross-fetch";
import { normalize, schema } from "normalizr";
import { camelizeKeys } from "humps";
import merge from "lodash/merge";

/* To be treated as an API call, the "action" should have the following structure:
*  
action = {-+
    // mandatory named property
    callApi : {
        // mandatory
        actions : {
            request: {
                type:  'SOME_REQUEST_TYPE'
            },
            success:{
                type: 'SOME_SUCCESS_TYPE'
            },
            failure: {
                type:  'SOME_FAILURE_TYPE'
            }
        }
        //mandatory
        endpoint: 'someurl.com/product/color',
        //optional (but recommended)
        schema: ... (object returned from a Normalizr schema creation),
        //optional - same object structure as defined on fetch MDN documentation
        options: {
            headers : ...,
            method : ...,
            mode : ...,
            credentials: ...
        } 
    }
}
*/
const HTTP_STRING = "http://";
const HTTPS_STRING = "https://";

// Default API root...
const API_ROOT =
	window.configData && window.configData.baseURL
		? window.configData.baseURL
		: "";

// API call logic
// Headers and other default or mandatory request values are defined here.
// Fetches an API response and normalizes the result JSON according to schema.
// This makes every API response have the same shape, regardless of how nested it was.
const callApi = (endpoint, payload, schema, options) => {
	const headers = {
		"Content-Type": "application/json"
	};
	const method = "GET";
	const mode = "cors";
	const credentials = "include";

	const defaultOptions = {
		headers,
		method,
		mode,
		credentials
	};

	// It always overrides headers and mode options. Otherwise the API request may not work...
	const fetchOptions = options
		? merge({}, { headers, mode }, options)
		: defaultOptions;

	let query = "";
	if (payload) {
		if (fetchOptions.method === "GET" || fetchOptions.method === "DELETE") {
			// Converts the payload to querystring. HTTP GET and DELETE methods should not have a body
			query = queryString.stringify(payload);
		} else {
			// 'POST','PUT', Adds the payload to the body as json
			fetchOptions.body = JSON.stringify(payload);
		}
	}

	const url = query ? `${endpoint}?${query}` : endpoint;
	const fullUrl =
		endpoint.indexOf(HTTP_STRING) === -1 && endpoint.indexOf(HTTPS_STRING) === -1
			? API_ROOT + url
			: url;

	return fetch(fullUrl, fetchOptions).then((response) =>
		response.json().then((json) => {
			if (!response.ok) {
				return Promise.reject(json);
			}

			// Our API response keys usually comes pascalized, but javascript convention is camelCase
			const camelizedJson = camelizeKeys(json);
			// const nextPageUrl = getNextPageUrl(response);

			const convertedJson = merge(
				{},
				schema ? normalize(camelizedJson, schema) : camelizedJson
				// { nextPageUrl }
			);
			return convertedJson;
		})
	);
};

// Middleware creator:
// A Redux middleware that interprets actions with CALL_API info specified.
// Performs the call and promises when such actions are dispatched.
const apiCallMiddleware = (store) => (next) => (action) => {
	const callAPI = action[CALL_API];
	if (typeof callAPI === "undefined") {
		return next(action);
	}

	let { endpoint } = callAPI;
	const { actions, payload, schema, options } = callAPI;

	if (typeof endpoint === "function") {
		endpoint = endpoint(store.getState());
	}

	if (typeof endpoint !== "string") {
		throw new Error("Specify a string endpoint URL.");
	}
	// Uncomment this if we want enforce Normalizr's Schema usage
	//  if (!schema) {
	//    throw new Error('Specify one of the exported Schemas.')
	// }
	if (typeof actions !== "object") {
		throw new Error("Expected an object");
	}
	if (!actions.request || !actions.success || !actions.failure) {
		throw new Error("The call API object should have three actions.");
	}
	if (
		!Object.values(actions).every(
			(action) =>
				typeof action === "object" && action.type && typeof action.type === "string"
		)
	) {
		throw new Error("Expected action to be object and have type attribute.");
	}

	const actionWith = (data) => {
		const finalAction = Object.assign({}, action, data);
		delete finalAction[CALL_API];
		return finalAction;
	};

	const { request, success, failure } = actions;
	next(actionWith(request));

	return callApi(endpoint, payload, schema, options).then(
		(response) => next(actionWith(merge({}, { response }, success))),
		(error) =>
			next(
				actionWith(
					merge(
						{},
						{ error: error || "Api Middleware: Something bad happened" },
						failure
					)
				)
			)
	);
};

// Action key that carries API call info interpreted by this Redux middleware.
export const CALL_API = "callApi";

export default apiCallMiddleware;
